// CETRA-38: keep legacy effect timing while clearing release ESLint warnings.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect } from 'react';
import {
    Dialog,
    Button,
    TextField,
    Typography,
    Box,
    Autocomplete,
    Radio,
    RadioGroup,
    FormControlLabel,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { designTokens } from '../../../theme';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import UserApi from '../../../rest-api/user-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import { isAuthExpiredDialogVisible, subscribeAuthExpired } from '../../../utils/auth-error-utils';
import {
    formatPatientAutocompleteLabel,
    patientAutocompleteNoOptionsText,
    patientAutocompleteSx,
    renderPatientAutocompleteOption,
} from '../../UIControls/patient-autocomplete-option';
import { CLIENT_NAME_LABEL, PATIENT_NAME_LABEL } from '../../helper/patientLabels';

export default function PatientDetailsDialog({ open, onClose, onSubmit, provetPatients = [], provetClient = null }) {
    const { authStore } = useContext(StoreContext);

    const [inputMethod, setInputMethod] = useState('manual'); // 'manual' or 'select'
    const [manualPatientName, setManualPatientName] = useState('');
    const [manualClientSurname, setManualClientSurname] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedProvetPatient, setSelectedProvetPatient] = useState(null);
    const [patients, setPatients] = useState([]);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);
    const hasProvetPatients = Array.isArray(provetPatients) && provetPatients.length > 0;
    const isSingleProvetPatient = hasProvetPatients && provetPatients.length === 1;
    const showProvetOption = hasProvetPatients;

    useEffect(() => {
        // Fetch patients from API when dialog opens
        let isSubscribed = true;

        const fetchPatients = async () => {
            setIsLoadingPatients(true);
            try {
                const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                    filterType: 'patientStatus',
                    filterValue: 'active',
                });

                if (!isSubscribed) {
                    return;
                }

                if (response?.patients) {
                    setPatients(response.patients);
                    if (response.patients.length === 0) {
                        setInputMethod('manual');
                    }
                } else {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } catch (error) {
                console.error('Failed to fetch patients:', error);
                if (isSubscribed) {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } finally {
                if (isSubscribed) {
                    setIsLoadingPatients(false);
                }
            }
        };

        if (open) {
            fetchPatients();
        }

        return () => {
            isSubscribed = false;
        };
    }, [open, authStore.idToken]);

    useEffect(() => {
        if (!hasProvetPatients) {
            setSelectedProvetPatient(null);
            return;
        }
        if (provetPatients.length === 1) {
            setSelectedProvetPatient(provetPatients[0]);
            return;
        }
        setSelectedProvetPatient(null);
    }, [provetPatients, hasProvetPatients]);

    const resetForm = () => {
        setInputMethod('manual');
        setManualPatientName('');
        setManualClientSurname('');
        setSelectedPatient(null);
        setSelectedProvetPatient(null);
    };

    const handleSubmit = () => {
        let patientDetails = null;
        let meta = {};

        if (inputMethod === 'manual') {
            if (manualPatientName || manualClientSurname) {
                patientDetails = {
                    patientName: manualPatientName || null,
                    clientSurname: manualClientSurname || null,
                };
            }
        } else if (inputMethod === 'select' && selectedPatient) {
            patientDetails = {
                patientName: selectedPatient.patientName || null,
                clientSurname: selectedPatient.clientSurname || null,
                patientId: selectedPatient.patientId || null,
                microchipNumber: selectedPatient.microchipNumber || null,
                clinicPatientId: selectedPatient.clinicPatientId || null,
            };
        } else if (inputMethod === 'provet' && selectedProvetPatient) {
            const provetClientLastName =
                provetClient?.lastName || provetClient?.lastname || provetClient?.surname || null;
            patientDetails = {
                patientName: selectedProvetPatient.name || null,
                clientSurname: provetClientLastName,
            };
            meta = { integrationSource: 'provet', provetPatient: selectedProvetPatient };
        }

        onSubmit(patientDetails, meta);
        resetForm();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (isAuthExpiredDialogVisible()) {
            handleClose();
            return undefined;
        }

        return subscribeAuthExpired(() => {
            handleClose();
        });
    }, [open, handleClose]);

    const isSubmitDisabled = () => {
        if (inputMethod === 'manual') {
            return !manualPatientName && !manualClientSurname;
        }
        if (inputMethod === 'select') {
            return !selectedPatient;
        }
        if (inputMethod === 'provet') {
            return !selectedProvetPatient;
        }
        return true;
    };

    const isBlocked = isAuthExpiredDialogVisible();

    const inputFieldSx = {
        '& .MuiOutlinedInput-root': {
            height: { xs: 'auto', sm: 52 },
            minHeight: { xs: '36px', sm: 'auto' },
            borderRadius: { xs: '8px', sm: '14px' },
            backgroundColor: '#FFFFFF',
            '& fieldset': { borderColor: '#E5E5E5', borderWidth: { xs: '0.6px', sm: '1.5px' } },
            '&:hover fieldset': { borderColor: '#E5E5E5' },
            '&.Mui-focused fieldset': { borderColor: '#FF669B' },
        },
    };

    const inputFieldPropsSx = {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: '14px', sm: '16px' },
        letterSpacing: { xs: '-0.56px', sm: '-0.64px' },
        color: '#616063',
        borderRadius: { xs: '8px', sm: '14px' },
        height: { xs: 'auto', sm: '52px' },
        minHeight: { xs: '36px', sm: 'auto' },
        alignItems: 'center',
    };

    const radioLabelSx = {
        '& .MuiFormControlLabel-label': {
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: 14, sm: 18 },
            lineHeight: { xs: '14px', sm: '18px' },
            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
            color: designTokens.colors.textSecondary,
        },
    };

    const continueButtonSx = {
        backgroundColor: '#FF669B',
        color: '#FFFFFF',
        borderRadius: { xs: '100px', sm: 36 },
        width: { xs: '100%', sm: '160px' },
        height: { xs: '35px', sm: '44px' },
        fontSize: { xs: 14, sm: 16 },
        fontWeight: 500,
        lineHeight: { xs: '14px', sm: '16px' },
        letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
        textTransform: 'none',
        '&:hover': { backgroundColor: '#e65588' },
        '&.Mui-disabled': { backgroundColor: 'rgba(255, 102, 155, 0.5)', color: '#FFFFFF' },
    };

    return (
        <>
            <Dialog
                open={open && !isBlocked}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        borderRadius: '30px !important',
                        padding: { xs: '30px 20px', sm: '30px 28px' },
                        width: { xs: 'calc(100% - 32px)', sm: '711px' },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: '711px' },
                        maxHeight: 'calc(100dvh - 32px)',
                        boxSizing: 'border-box',
                        overflowY: 'auto',
                    },
                }}
                maxWidth={false}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Header: title + X button */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: { xs: '8px', sm: '24px' },
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: { xs: 26, sm: 32 },
                                lineHeight: { xs: '26px', sm: '32px' },
                                letterSpacing: { xs: '-1.04px', sm: '-1.28px' },
                                color: designTokens.colors.textPrimary,
                                pr: '44px',
                            }}
                        >
                            Add patient details
                        </Typography>
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                p: 0,
                                width: { xs: '28px', sm: '36px' },
                                height: { xs: '28px', sm: '36px' },
                                background: 'rgb(246, 246, 246)',
                                borderRadius: { xs: '11.928px', sm: '50%' },
                                flexShrink: 0,
                            }}
                        >
                            <CloseIcon sx={{ width: { xs: '16px', sm: '24px' }, height: { xs: '16px', sm: '24px' } }} />
                        </IconButton>
                    </Box>

                    {/* Subtitle */}
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: { xs: 14, sm: 18 },
                            lineHeight: { xs: '18px', sm: '18px' },
                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                            color: designTokens.colors.textSecondary,
                            mb: { xs: '16px', sm: '30px' },
                        }}
                    >
                        Add patient details to shared handout
                    </Typography>

                    {/* Radio options */}
                    <RadioGroup
                        value={inputMethod}
                        onChange={(e) => setInputMethod(e.target.value)}
                        sx={{ mb: { xs: '18px', sm: '28px' }, gap: { xs: 0.75, sm: 1 } }}
                    >
                        <FormControlLabel
                            value="manual"
                            control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                            label="Enter patient details"
                            sx={radioLabelSx}
                        />
                        <FormControlLabel
                            value="select"
                            control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                            label="Select existing patient"
                            disabled={isLoadingPatients || patients.length === 0}
                            sx={radioLabelSx}
                        />
                        {showProvetOption && (
                            <FormControlLabel
                                value="provet"
                                control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                label="Choose a Provet patient"
                                sx={radioLabelSx}
                            />
                        )}
                    </RadioGroup>

                    {/* Input fields */}
                    {inputMethod === 'manual' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 14, sm: 18 },
                                        lineHeight: { xs: '14px', sm: '18px' },
                                        letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                        color: designTokens.colors.textSecondary,
                                        mb: { xs: '8px', sm: '12px' },
                                    }}
                                >
                                    {PATIENT_NAME_LABEL}
                                </Typography>
                                <TextField
                                    value={manualPatientName}
                                    onChange={(e) => setManualPatientName(e.target.value)}
                                    fullWidth
                                    sx={inputFieldSx}
                                    InputProps={{ sx: inputFieldPropsSx }}
                                    inputProps={{ className: 'mp-mask-patient' }}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 14, sm: 18 },
                                        lineHeight: { xs: '14px', sm: '18px' },
                                        letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                        color: designTokens.colors.textSecondary,
                                        mb: { xs: '8px', sm: '12px' },
                                    }}
                                >
                                    {CLIENT_NAME_LABEL}
                                </Typography>
                                <TextField
                                    value={manualClientSurname}
                                    onChange={(e) => setManualClientSurname(e.target.value)}
                                    fullWidth
                                    sx={inputFieldSx}
                                    InputProps={{ sx: inputFieldPropsSx }}
                                    inputProps={{ className: 'mp-mask-patient' }}
                                />
                            </Box>
                        </Box>
                    )}

                    {inputMethod === 'select' && (
                        <Autocomplete
                            options={patients}
                            getOptionLabel={formatPatientAutocompleteLabel}
                            value={selectedPatient}
                            onChange={(event, newValue) => setSelectedPatient(newValue)}
                            noOptionsText={patientAutocompleteNoOptionsText}
                            renderOption={renderPatientAutocompleteOption}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select patient"
                                    placeholder="Search patients..."
                                    sx={inputFieldSx}
                                    InputProps={{ ...params.InputProps, sx: inputFieldPropsSx }}
                                    inputProps={{ ...params.inputProps, className: 'mp-mask-patient' }}
                                />
                            )}
                            fullWidth
                            disabled={isLoadingPatients || patients.length === 0}
                            sx={patientAutocompleteSx}
                        />
                    )}

                    {inputMethod === 'provet' && showProvetOption && (
                        <Autocomplete
                            options={provetPatients}
                            getOptionLabel={(option) =>
                                `${option?.name || ''}${option?.id ? ` (#${option.id})` : ''}`.trim()
                            }
                            isOptionEqualToValue={(option, value) => option?.url === value?.url}
                            value={selectedProvetPatient}
                            onChange={(event, newValue) => setSelectedProvetPatient(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Choose a patient"
                                    placeholder="Search Provet patients..."
                                    sx={inputFieldSx}
                                    InputProps={{ ...params.InputProps, sx: inputFieldPropsSx }}
                                    inputProps={{ ...params.inputProps, className: 'mp-mask-patient' }}
                                />
                            )}
                            fullWidth
                            disabled={isSingleProvetPatient}
                        />
                    )}

                    {!isLoadingPatients && patients.length === 0 && !showProvetOption && (
                        <Typography sx={{ fontSize: 12, color: designTokens.colors.textSecondary, mt: 1 }}>
                            No patients found. Use manual entry to add patient details.
                        </Typography>
                    )}

                    {/* Continue button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: '24px', sm: '28px' } }}>
                        <Button sx={continueButtonSx} onClick={handleSubmit} disabled={isSubmitDisabled()}>
                            Continue
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <BlockingSpinner open={open && !isBlocked && isLoadingPatients} />
        </>
    );
}
